import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import moment from 'moment'
import { ArrowDownTrayIcon, ArrowLongLeftIcon, DocumentChartBarIcon } from '@heroicons/react/24/outline'

import { useGetCheckupResource } from 'api/checkups/getCheckupResource'
import { useExportFile } from 'hooks'
import { notification } from 'helpers'
import { SABRE_KEY, TRAVEL_PORT_KEY } from 'constants/gds'
import { Checkup } from 'types/domain/Checkup'
import { CheckupLogRate } from 'types/domain/CheckupLog'
import PageLoader from 'ui/PageLoader'
import { Button, Header, Tabs } from 'ui-v2'
import BookingCodeList from 'modules/HotelsModule/pages/HotelPage/components/BookingCodeList/BookingCodeList'
import RateDetailsTab from 'modules/HotelsModule/pages/HotelPage/components/RateDetailsTab'
import RateRulesTab from 'modules/HotelsModule/pages/HotelPage/components/RateRulesTab'
import Unavailable from 'modules/HotelsModule/pages/HotelPage/components/Unavailable'

const RATE_DETAILS_TAB = {
  key: 'details',
  label: 'Rate Details',
}

const RATE_RULES_TAB = {
  key: 'rules',
  label: 'Rate Rules',
}

const TABS = [RATE_DETAILS_TAB, RATE_RULES_TAB]
const DEFAULT_TAB_KEY = RATE_DETAILS_TAB.key

const normalizeCodes = (codes?: string[] | string | null) => {
  if (Array.isArray(codes)) {
    return codes.filter(Boolean)
  }

  if (typeof codes === 'string') {
    return codes
      .split('|')
      .map((value) => value.trim())
      .filter(Boolean)
  }

  return []
}

const getGdsKey = (gdsName?: string) => {
  const value = gdsName?.toLowerCase() || ''

  if (value.includes('sabre')) {
    return SABRE_KEY
  }

  if (value.includes('travel')) {
    return TRAVEL_PORT_KEY
  }

  return ''
}

const SummaryItem = ({ label, value }: { label: string; value?: string }) => (
  <div>
    <div className="text-xs leading-none font-normal text-gray-500 mb-1">{label}</div>
    <div className="text-base leading-6 font-normal text-gray-700 break-words">{value || '-'}</div>
  </div>
)

const getRequesterValue = ({
  requester_name,
  requester_email,
}: {
  requester_name?: string
  requester_email?: string
}) => requester_name?.trim() || requester_email?.trim() || '-'

const CheckupLogDetails = () => {
  const { push } = useHistory()
  const { search } = useLocation()
  const { checkupId, tabKey } = useParams<{ checkupId: string; tabKey?: string }>()
  const [selectedRate, setSelectedRate] = useState<Nullable<CheckupLogRate>>(null)
  const { isLoading: isExporting, saveFile } = useExportFile()
  const { isLoading: isExportingRaw, saveFile: saveRawFile } = useExportFile()

  const activeTabKey = TABS.some((tab) => tab.key === tabKey) ? tabKey || DEFAULT_TAB_KEY : DEFAULT_TAB_KEY

  const { data, isLoading, isFetching } = useGetCheckupResource({
    params: {
      id: Number(checkupId),
    },
    queryOptions: {
      enabled: Number.isFinite(Number(checkupId)),
      onError: (error) => notification.error({ message: error.message }),
    },
  })

  useEffect(() => {
    const rates = data?.data.rates || []

    setSelectedRate((previousRate) => {
      if (!previousRate) {
        return rates[0]
      }

      return rates.find((rate) => rate.id === previousRate.id || rate.rate_plan === previousRate.rate_plan) || rates[0]
    })
  }, [data?.data.rates])

  const checkup = useMemo<Checkup | undefined>(() => {
    if (!data?.data) {
      return undefined
    }

    return {
      id: data.data.id,
      from_date: data.data.from_date,
      to_date: data.data.to_date,
      created_at: data.data.created_at,
      adults: data.data.adults,
      children: data.data.children,
      corporate_codes: normalizeCodes(data.data.corporate_codes),
      rate_category_codes: normalizeCodes(data.data.rate_category_codes),
    }
  }, [data?.data])

  const gdsKey = useMemo(() => getGdsKey(data?.data.gds_name), [data?.data.gds_name])
  const rateCategoryCodes = useMemo(
    () => normalizeCodes(data?.data.rate_category_codes),
    [data?.data.rate_category_codes],
  )
  const hasRawResponses = (data?.data.raw_responses_count || 0) > 0

  const handleBack = useCallback(() => {
    push(`/admin/logs${search}`)
  }, [push, search])

  const handleSelectTab = useCallback(
    (nextTabKey: string) => {
      push(`/admin/logs/${checkupId}/${nextTabKey}${search}`)
    },
    [checkupId, push, search],
  )

  const handleExport = useCallback(() => {
    if (!data?.data) {
      return Promise.resolve()
    }

    const fileName = [
      data.data.hotel_name || 'Rate check history',
      data.data.gds_name || 'Unknown GDS',
      `#${data.data.id}`,
      moment(data.data.created_at).format('YYYY-MM-DD HH.mm'),
    ].join(' - ')

    return saveFile('/exports/checkup-log-details', `${fileName}.xlsx`, {
      type: 'checkup-log-details',
      checkup_id: data.data.id,
    })
  }, [data?.data, saveFile])

  const handleExportRaw = useCallback(() => {
    if (!data?.data) {
      return Promise.resolve()
    }

    const fileName = [
      data.data.hotel_name || 'Rate check history',
      data.data.gds_name || 'Unknown GDS',
      `#${data.data.id}`,
      moment(data.data.created_at).format('YYYY-MM-DD HH.mm'),
      'raw',
    ].join(' - ')

    return saveRawFile('/exports/checkup-log-raw', `${fileName}.zip`, {
      type: 'checkup-log-raw',
      checkup_id: data.data.id,
    })
  }, [data?.data, saveRawFile])

  if (isLoading) {
    return <PageLoader />
  }

  if (!data?.data) {
    return (
      <div className="px-2 py-4 md:px-7">
        <Unavailable title="Rate check history entry not found">
          The requested rate check history entry could not be loaded.
        </Unavailable>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 bg-bg-light">
      <Header title={`Rate check history #${data.data.id}`} size="big">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" className="px-4" onClick={handleBack}>
            <span className="inline-flex items-center gap-2">
              <ArrowLongLeftIcon className="w-4" />
              Back to Rate check history
            </span>
          </Button>

          <Button variant="primary" className="px-4" onClick={handleExport} isLoading={isExporting}>
            <span className="inline-flex items-center gap-2">
              Export
              <DocumentChartBarIcon className="w-4" />
            </span>
          </Button>

          <Button
            variant="default"
            className="px-4"
            onClick={handleExportRaw}
            isLoading={isExportingRaw}
            isDisabled={!hasRawResponses}
          >
            <span className="inline-flex items-center gap-2">
              Export raw
              <ArrowDownTrayIcon className="w-4" />
            </span>
          </Button>
        </div>
      </Header>

      <div className="mx-2 rounded-lg bg-white p-4 md:mx-7 md:p-6">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <SummaryItem label="Hotel" value={data.data.hotel_name} />
          <SummaryItem label="Property ID" value={data.data.property_id} />
          <SummaryItem
            label="Requester"
            value={getRequesterValue({
              requester_name: data.data.requester_name,
              requester_email: data.data.requester_email,
            })}
          />
          <SummaryItem label="Requester email" value={data.data.requester_email} />
          <SummaryItem label="GDS" value={data.data.gds_name} />
          <SummaryItem
            label="Stay"
            value={`${moment(data.data.from_date).format('DD.MM.YYYY')} - ${moment(data.data.to_date).format(
              'DD.MM.YYYY',
            )}`}
          />
          <SummaryItem
            label="Guests"
            value={`${data.data.adults} adults${data.data.children ? `, ${data.data.children} children` : ''}`}
          />
          <SummaryItem label="RAC" value={normalizeCodes(data.data.corporate_codes).join(', ') || '-'} />
          <SummaryItem label="Rate categories" value={rateCategoryCodes.join(', ') || '-'} />
          <SummaryItem label="Created" value={moment(data.data.created_at).format('DD.MM.YYYY HH:mm')} />
        </div>
      </div>

      {!hasRawResponses && (
        <div className="mx-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900 md:mx-7 md:px-6">
          No raw GDS responses were stored for this checkup.
        </div>
      )}

      {(data.data.rates || []).length > 0 ? (
        <div className="bg-bg-light pb-4 px-2 flex flex-col gap-4 md:px-7 xl:flex-row">
          <BookingCodeList
            selectedRate={selectedRate}
            onSelectRate={(rate) => setSelectedRate(rate as CheckupLogRate)}
            rateList={data.data.rates}
            disabled={isFetching}
          />

          <div className="flex-1">
            <div className="flex items-center justify-start">
              <Tabs items={TABS} onChange={handleSelectTab} selectedKey={activeTabKey} className="pt-0" />
            </div>

            <div className="bg-white px-2 py-5 flex flex-col gap-5 lg:px-4">
              {activeTabKey === RATE_DETAILS_TAB.key && selectedRate && (
                <RateDetailsTab rateDetails={selectedRate} isLoading={isFetching} checkup={checkup} gdsKey={gdsKey} />
              )}

              {activeTabKey === RATE_RULES_TAB.key &&
                selectedRate &&
                (selectedRate.rate_details ? (
                  <RateRulesTab
                    rateDetails={selectedRate}
                    isLoading={isFetching}
                    rateRules={selectedRate.rate_details}
                    checkup={checkup}
                    gdsKey={gdsKey}
                  />
                ) : (
                  <Unavailable title="No rules saved for this rate.">
                    This log contains only the stored rate search result.
                  </Unavailable>
                ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="px-2 pb-4 md:px-7">
          <div className="bg-white py-9">
            <Unavailable title="No rates saved for this checkup.">
              This log does not contain any stored records in `checkup_hotel_rates`.
            </Unavailable>
          </div>
        </div>
      )}
    </div>
  )
}

export default CheckupLogDetails
